/* eslint-disable @typescript-eslint/indent */
/* eslint-disable no-nested-ternary */
import ButtonPrimary from '@components/common/ButtonPrimary';
import {
  AMPLITUDE_EXPERIMENT_CONTROL_NAME,
  AMPLITUDE_EXPERIMENT_VARIANT_NAME,
  AMPLITUDE_EXPERIMENT_VARIANT_NAME_2,
} from '@constants/amplitudeExperiments';
import actionTypes from '@context/actionTypes';
import { useFeatureFlagContext } from '@context/FeatureFlagContext';
import { store } from '@context/store';
import { CartItemDetails } from '@customTypes/cart-items';
import HotelDetails from '@customTypes/hotel-details';
import Product from '@customTypes/product';
import { GuestGroupType } from '@customTypes/product-details';
import { isProductUnAvailable } from '@helpers/product';
import { openDatePickerWithFilterApplied } from '@helpers/smartCalendar';
import Button from '@solar/common/Button/ButtonV2';
import { isToday } from 'date-fns';
import { isEmpty } from 'lodash';
import { useRouter } from 'next/router';
import React, { useContext, useMemo, useState } from 'react';

type Props = {
  adultCounter: number;
  cartData: CartItemDetails | undefined;
  date: Date | undefined;
  hotelDetails: HotelDetails;
  onButtonClick: () => Promise<unknown>;
  product: Product;
  guestsGroupBag?: GuestGroupType[];
  selectedTimeSlot?: Product;
};

const defaultProps = {
  guestsGroupBag: undefined,
  selectedTimeSlot: undefined,
};

export default function ProductDetailsFooter({
  adultCounter,
  date,
  onButtonClick,
  hotelDetails,
  product,
  cartData,
  guestsGroupBag,
  selectedTimeSlot,
}: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { dispatch } = useContext(store);
  const router = useRouter();
  const { ctasUpdateExperiment } = useFeatureFlagContext();

  // Set availability
  const isUnAvailable = useMemo(() => {
    if (!product) {
      return true;
    }

    return isProductUnAvailable(selectedTimeSlot || product, cartData, hotelDetails, date);
  }, [product, cartData, hotelDetails, date, selectedTimeSlot]);

  const buttonClick = async () => {
    setIsLoading(true);

    if (!date || isUnAvailable) {
      openDatePickerWithFilterApplied(product, dispatch, true);
      setIsLoading(false);
      return;
    }

    await onButtonClick();

    setIsLoading(false);

    if (date && isToday(date)) {
      dispatch({
        type: actionTypes.OPEN_SAME_DAY_BOOKING_WARNING,
        payload: true,
      });
    } else {
      router.push('/users/guest-billing-detail');
    }
  };

  const guestsCount = useMemo(
    () =>
      guestsGroupBag &&
      guestsGroupBag.reduce((sum: number, item: any) => sum + item.adults + item.children, 0),
    [guestsGroupBag],
  );

  const isDisabled = useMemo(() => {
    if (product.is_product_time_group) {
      if (!selectedTimeSlot) {
        return true;
      }
      if (!date || adultCounter > 0) {
        return false;
      }

      if (date && isUnAvailable) {
        return false;
      }

      return true;
    }

    if (product.individual_billing) {
      if ((date && !isUnAvailable && adultCounter > 0) || (date && isUnAvailable) || !date) {
        return false;
      }

      return true;
    }

    if (!product.individual_billing) {
      if (date && !isUnAvailable && (isEmpty(guestsGroupBag) || guestsCount === 0)) {
        return true;
      }
      if (!guestsGroupBag?.every((group) => group.adults > 0)) {
        return true;
      }

      return false;
    }

    return false;
  }, [adultCounter, date, guestsGroupBag, guestsCount, isUnAvailable, product, selectedTimeSlot]);

  // Update button text
  const footerText = useMemo(() => {
    if (!date) {
      return 'Select a Date';
    }

    if (isUnAvailable) {
      return 'Change Date';
    }

    if (
      ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME ||
      ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME_2
    ) {
      return 'Reserve';
    }

    return 'Book Now';
  }, [ctasUpdateExperiment, date, isUnAvailable]);

  return (
    <div className="absolute backdrop-filter backdrop-blur-27px bg-white bg-opacity-84 border-rp-gray-divider border-t bottom-0 flex justify-between p-4 w-full pb-6">
      {!ctasUpdateExperiment || ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_CONTROL_NAME ? (
        <ButtonPrimary onClick={buttonClick} isDisabled={isLoading || isDisabled} classes="h-12">
          {footerText}
        </ButtonPrimary>
      ) : (
        <Button
          size="lg"
          onClick={buttonClick}
          variant={
            ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME ? 'primary' : 'ultramarine'
          }
        >
          {footerText}
        </Button>
      )}
    </div>
  );
}

ProductDetailsFooter.defaultProps = defaultProps;
